import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import type { VueDAigle } from '../../types.js';
import commun from './commun.module.css';
import { LienConsole, Montant, NonComptees, Section } from './composants.js';
import { useDroitsVue } from './droits.js';
import { useFormats } from './formats.js';
import styles from './CoutsMarge.module.css';

/**
 * Les coûts du mois par poste, le MRR et la marge. Un poste « à saisir » n'est
 * pas dans le total ; une marge `null` (aucun coût saisi) invite à saisir les
 * coûts — par un lien pour qui en a le droit, par un texte sinon.
 */
export function CoutsMarge({ vue, numero }: { vue: VueDAigle; numero: string }): JSX.Element {
  const { t } = useTranslation('console');
  const f = useFormats();
  const { peut } = useDroitsVue(vue);
  const { couts, indicateurs } = vue;
  const marge = indicateurs.margeMoisCentimes;
  const peutSaisir = peut('console:manage-costs');
  const aucunMontant =
    couts.parPoste.length === 0 || couts.parPoste.every((poste) => poste.montantCentimes === null);
  const postesASaisir = couts.parPoste.filter((poste) => poste.montantCentimes === null).length;

  const invite = peutSaisir ? (
    <LienConsole vers="/finances?onglet=couts">{t('vueDAigle.couts.saisir')}</LienConsole>
  ) : (
    <span>{t('vueDAigle.couts.nonSaisis')}</span>
  );

  return (
    <Section
      id="couts"
      numero={numero}
      titre={t('vueDAigle.couts.titre')}
      note={t('vueDAigle.couts.note', { mois: f.moisLong(couts.mois) })}
      actions={
        peutSaisir ? (
          <LienConsole vers="/finances?onglet=couts" bouton>
            {t('vueDAigle.couts.saisir')}
          </LienConsole>
        ) : undefined
      }
    >
      <div className={commun.tableauConteneur}>
        <table className={commun.table}>
          <caption className={commun.srOnly}>{t('vueDAigle.couts.caption')}</caption>
          <thead>
            <tr>
              <th scope="col">{t('vueDAigle.couts.colPoste')}</th>
              <th scope="col" className={commun.num}>
                {t('vueDAigle.couts.colMontant')}
              </th>
            </tr>
          </thead>
          <tbody>
            {couts.parPoste.length === 0 ? (
              <tr>
                <td colSpan={2} className={commun.celluleMuette}>
                  {t('vueDAigle.couts.vide')}
                </td>
              </tr>
            ) : (
              couts.parPoste.map((poste) => (
                <tr key={poste.poste}>
                  <th scope="row">{poste.poste}</th>
                  <td className={commun.num}>
                    <Montant centimes={poste.montantCentimes} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
          <tfoot>
            <tr>
              <th scope="row">{t('vueDAigle.couts.total')}</th>
              <td className={commun.num}>
                {aucunMontant ? (
                  <span className={commun.aSaisir}>{t('vueDAigle.aSaisir')}</span>
                ) : (
                  f.euros(couts.totalCentimes)
                )}
              </td>
            </tr>
            <tr className={styles.secondaire}>
              <th scope="row">{t('vueDAigle.couts.mrr')}</th>
              <td className={commun.num}>{f.euros(indicateurs.mrrCentimes)}</td>
            </tr>
            <tr className={commun.marge}>
              <th scope="row">{t('vueDAigle.couts.marge')}</th>
              <td className={commun.num}>{marge === null ? invite : f.euros(marge)}</td>
            </tr>
            <tr className={styles.secondaire}>
              <th scope="row">{t('vueDAigle.couts.taux')}</th>
              <td className={commun.num}>
                {marge !== null && indicateurs.mrrCentimes > 0 ? (
                  f.pourcent(marge / indicateurs.mrrCentimes)
                ) : (
                  <span className={commun.aSaisir}>—</span>
                )}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      <NonComptees nombre={postesASaisir} />
      <p className={commun.noteDiscrete}>{t('vueDAigle.couts.noteBas')}</p>
    </Section>
  );
}
